import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { FollowListings } from '~/common/components/follow-listings';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import type { FollowOperationLocation } from '~/common/types/follow';
import type { FollowListModalType } from '~/common/types/follow';
import { Color } from '~/utils/styles/enums';

interface Props {
  userId: string;
  type: FollowListModalType;
  handleTabClick: (type: FollowListModalType) => void;
  isOpen: boolean;
  onClose: () => void;
  onLoginNeededOnFollow?: (
    userId: string,
    followButtonPlacementLocation: FollowOperationLocation
  ) => void;
}

export const FollowListingsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userId,
  type,
  handleTabClick,
  onLoginNeededOnFollow,
}) => {
  const { session } = useTellerSession();

  // As we show fixed ad on bottom, adjust modal size so everything looks nice
  // (if user is VIP, ad is not shown)
  const maxHeight = useMemo(() => {
    if (session && session?.tellerUser?.isVipUser) {
      return '90%';
    }
    return '70%';
  }, [session]);

  const marginTop = useMemo(() => {
    if (session && session?.tellerUser?.isVipUser) {
      return [0, 20];
    }
    return [0, 0];
  }, [session]);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="auto"
        backdropBlur="20px"
      />
      <ModalContent
        mt={marginTop}
        height={maxHeight}
        maxHeight={maxHeight}
        padding={{
          base: '16px',
        }}
      >
        <CloseIcon />
        <FollowListings
          userId={userId}
          type={type}
          handleTabClick={handleTabClick}
          onLoginNeededOnFollow={onLoginNeededOnFollow}
        />
      </ModalContent>
    </Modal>
  );
};

const CloseIcon = styled(ModalCloseButton)`
  position: absolute;
  top: -35px;
  margin-left: auto;
  right: 0;

  > svg {
    color: ${Color.WHITE};
    height: 20px;
    width: 20px;
  }

  &:focus,
  &:hover,
  &:active {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-appearance: none;
    outline: 0;
    background: none;
    border: none;
    border-color: black;
    box-shadow: none;
  }
`;
